'use client'

import { useEffect, useMemo, useRef } from 'react'
import { cn } from '@/lib/utils'
import { slashCommandRegistry, type SlashCommand } from '@/lib/slash-commands'

type SlashCommandPickerProps = {
  input: string
  open: boolean
  selectedIndex: number
  onSelectedIndexChange: (index: number) => void
  onSelect: (command: SlashCommand) => void
  includeAdmin?: boolean
  registryVersion?: number
}

function argsPreview(command: SlashCommand): string {
  if (command.args.length === 0) return ''
  return command.args
    .map(arg => arg.required ? `<${arg.name}>` : `[${arg.name}]`)
    .join(' ')
}

export function SlashCommandPicker({
  input,
  open,
  selectedIndex,
  onSelectedIndexChange,
  onSelect,
  includeAdmin = false,
  registryVersion = 0,
}: SlashCommandPickerProps) {
  const query = input.slice(1).split(/\s+/)[0] ?? ''
  const commands = useMemo(() => {
    void registryVersion
    if (!input.startsWith('/')) return []
    return slashCommandRegistry.search(query, { includeAdmin }).slice(0, 8)
  }, [includeAdmin, input, query, registryVersion])

  useEffect(() => {
    if (selectedIndex >= commands.length) {
      onSelectedIndexChange(Math.max(commands.length - 1, 0))
    }
  }, [commands.length, onSelectedIndexChange, selectedIndex])

  // Refs to each row so we can scroll the keyboard-selected row into view.
  // Hooks must be called unconditionally; the early-return below would cause
  // a hook order mismatch otherwise.
  const rowRefs = useRef<Array<HTMLButtonElement | null>>([])

  useEffect(() => {
    const el = rowRefs.current[selectedIndex]
    if (el) el.scrollIntoView({ block: 'nearest' })
  }, [selectedIndex])

  if (!open || !input.startsWith('/') || commands.length === 0) return null

  return (
    // Width matches the prompt box: `w-full` inherits from the absolute
    // positioning context (the prompt-card div), so the picker spans the
    // full prompt width on every screen size. The previous `max-w-xl`
    // capped it at 576px even when the prompt was wider.
    // Colors use atrium-* tokens (NOT bg-white / hex muted) so light and
    // dark themes both render correctly via the CSS variables defined in
    // globals.css.
    <div className="absolute bottom-full left-0 z-50 mb-2 w-full overflow-hidden rounded-xl border border-atrium-border bg-atrium-inner shadow-lg ring-1 ring-atrium-black/5 dark:ring-black/40">
      <div className="max-h-72 overflow-y-auto p-1">
        {commands.map((command, index) => {
          const selected = index === selectedIndex
          const preview = argsPreview(command)

          return (
            <button
              key={command.name}
              ref={(el) => { rowRefs.current[index] = el }}
              type="button"
              onMouseEnter={() => onSelectedIndexChange(index)}
              onClick={() => onSelect(command)}
              className={cn(
                'flex w-full min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors',
                selected ? 'bg-atrium-content text-atrium-black' : 'text-atrium-black hover:bg-atrium-content/70'
              )}
            >
              <span className="font-mono text-sm font-semibold shrink-0">/{command.name}</span>
              {command.custom ? (
                <span className="shrink-0 rounded bg-atrium-content px-1.5 py-0.5 text-[10px] font-medium text-atrium-black/70">
                  Custom
                </span>
              ) : null}
              {preview ? (
                <span className="hidden font-mono text-xs text-atrium-muted shrink-0 sm:inline">
                  {preview}
                </span>
              ) : null}
              <span className="min-w-0 flex-1 truncate text-xs text-atrium-muted">
                {command.description}
              </span>
              <span
                className={cn(
                  'shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold',
                  command.tier === 'A'
                    ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                    : 'bg-blue-500/15 text-blue-700 dark:text-blue-300'
                )}
              >
                {command.tier === 'A' ? 'Fast' : 'AI'}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
